import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { mapActions } from "features/map";
import { getGebietColor } from "common/wencoGebietColors";
import { getDrivers } from "state/entities/members/selectors";
import {
  isWencoDemoModeEnabled,
  isWencoMapCustomersEnabled,
  WENCO_DEMO_MODE_CHANGED
} from "common/wencoDemoMode";

export function getShipmentCoordinates(shipment) {
  const lat = Number(
    shipment.recipient_address_latitude ??
      shipment.recipient?.address?.latitude
  );
  const lng = Number(
    shipment.recipient_address_longitude ??
      shipment.recipient?.address?.longitude
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return null;
  }
  return { lat, lng };
}

function getMemberIdFromShipment(shipment) {
  return (
    shipment?.driverId ||
    shipment?.responsible_member_id ||
    shipment?.last_responsible_member_id ||
    null
  );
}

function dedupeRepeatedLabel(text) {
  const parts = String(text || "")
    .trim()
    .split(/\s+/);
  if (parts.length < 2 || parts.length % 2 !== 0) {
    return String(text || "").trim();
  }
  const half = parts.length / 2;
  const firstHalf = parts.slice(0, half).join(" ");
  const secondHalf = parts.slice(half).join(" ");
  return firstHalf === secondHalf ? firstHalf : String(text || "").trim();
}

/** Map pin / hub label: Vorname only (no Nachname). */
export function getMemberDisplayName(member) {
  if (!member) return "";
  const first = String(member.firstname || "").trim();
  const last = String(member.lastname || "").trim();
  if (!first) return "";
  if (last && first !== last) {
    return first;
  }
  return dedupeRepeatedLabel(first);
}

function getShipmentGebiet(shipment) {
  return (
    shipment?.wenco?.gebiet ||
    shipment?.import_logisticcompany_external_label ||
    ""
  );
}

function getMemberHomePosition(member, customerPositions) {
  const homeLat = Number(member?.wenco?.homeLatitude);
  const homeLng = Number(member?.wenco?.homeLongitude);
  if (Number.isFinite(homeLat) && Number.isFinite(homeLng)) {
    return { lat: homeLat, lng: homeLng };
  }

  if (!customerPositions.length) {
    return null;
  }

  const sum = customerPositions.reduce(
    (acc, position) => ({
      lat: acc.lat + position.lat,
      lng: acc.lng + position.lng
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: sum.lat / customerPositions.length,
    lng: sum.lng / customerPositions.length
  };
}

/**
 * VisiTour-style territories: member home hub + customer points + spokes.
 */
export function getWencoMapTerritories(shipmentsMap = {}, membersMap = {}) {
  const customersByMember = new Map();
  const seenCustomers = new Set();

  Object.values(shipmentsMap).forEach(shipment => {
    const memberId = getMemberIdFromShipment(shipment);
    const customerKey =
      shipment.ext_reference || shipment.group_reference || shipment.id;
    const position = getShipmentCoordinates(shipment);

    if (!memberId || !customerKey || !position || seenCustomers.has(customerKey)) {
      return;
    }

    seenCustomers.add(customerKey);
    if (!customersByMember.has(memberId)) {
      customersByMember.set(memberId, []);
    }
    customersByMember.get(memberId).push({
      key: String(customerKey),
      position,
      gebiet: getShipmentGebiet(shipment)
    });
  });

  const hubs = [];
  const customers = [];
  const spokes = [];

  customersByMember.forEach((memberCustomers, memberId) => {
    const member = membersMap[memberId] ?? membersMap[String(memberId)] ?? null;
    const memberLabel = getMemberDisplayName(member) || String(memberId);
    const hubGebiet = memberCustomers[0]?.gebiet || "";
    const hubColor = getGebietColor(hubGebiet);
    const home = getMemberHomePosition(
      member,
      memberCustomers.map(entry => entry.position)
    );

    if (!home) {
      return;
    }

    hubs.push({
      key: `wenco-hub-${memberId}`,
      memberId: String(memberId),
      position: home,
      color: hubColor,
      gebiet: hubGebiet,
      memberLabel
    });

    memberCustomers.forEach(customer => {
      const color = getGebietColor(customer.gebiet);
      customers.push({
        key: `wenco-customer-${customer.key}`,
        memberId: String(memberId),
        position: customer.position,
        color,
        gebiet: customer.gebiet
      });
      spokes.push({
        key: `wenco-spoke-${memberId}-${customer.key}`,
        color,
        path: [home, customer.position]
      });
    });
  });

  return { hubs, customers, spokes };
}

function buildTerritoriesSyncKey(shipmentsMap = {}, membersMap = {}) {
  const { hubs, customers, spokes } = getWencoMapTerritories(
    shipmentsMap,
    membersMap
  );
  return [
    hubs
      .map(
        hub =>
          `${hub.key}:${hub.position.lat},${hub.position.lng}:${hub.memberLabel}`
      )
      .join("|"),
    customers
      .map(
        customer =>
          `${customer.key}:${customer.position.lat},${customer.position.lng}`
      )
      .join("|"),
    spokes.length
  ].join("||");
}

const WencoCustomersOnMap = ({ shipments, drivers, dispatch, active }) => {
  const [demoEnabled, setDemoEnabled] = useState(isWencoDemoModeEnabled());
  const [mapsReady, setMapsReady] = useState(
    () => typeof window !== "undefined" && Boolean(window.google?.maps)
  );
  const lastSyncKeyRef = useRef("");

  useEffect(() => {
    const onDemoChange = () => setDemoEnabled(isWencoDemoModeEnabled());
    window.addEventListener(WENCO_DEMO_MODE_CHANGED, onDemoChange);
    return () =>
      window.removeEventListener(WENCO_DEMO_MODE_CHANGED, onDemoChange);
  }, []);

  useEffect(() => {
    if (mapsReady) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      if (window.google?.maps) {
        setMapsReady(true);
        lastSyncKeyRef.current = "";
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [mapsReady]);

  const territoriesSyncKey = useMemo(
    () => buildTerritoriesSyncKey(shipments, drivers),
    [shipments, drivers]
  );

  const shouldShow =
    active &&
    demoEnabled &&
    isWencoMapCustomersEnabled() &&
    mapsReady &&
    territoriesSyncKey.length > 0;

  useEffect(() => {
    if (!shouldShow) {
      if (lastSyncKeyRef.current) {
        dispatch(mapActions.clear("scheduling"));
        lastSyncKeyRef.current = "";
      }
      return undefined;
    }

    if (lastSyncKeyRef.current === territoriesSyncKey) {
      return undefined;
    }

    const territories = getWencoMapTerritories(shipments, drivers);
    lastSyncKeyRef.current = territoriesSyncKey;

    const boundsPins = [
      ...territories.hubs.map(hub => ({
        key: hub.key,
        position: hub.position,
        useHtmlOverlay: true
      })),
      ...territories.customers.map(customer => ({
        key: customer.key,
        position: customer.position,
        useHtmlOverlay: true
      }))
    ];

    dispatch(
      mapActions.set(
        "scheduling",
        {
          pins: boundsPins,
          polygons: []
        },
        {
          fitBounds: true
        }
      )
    );

    return undefined;
  }, [shouldShow, territoriesSyncKey, shipments, drivers, dispatch]);

  useEffect(
    () => () => {
      if (lastSyncKeyRef.current) {
        dispatch(mapActions.clear("scheduling"));
        dispatch(
          mapActions.setDriversOptions({
            fitBounds: true
          })
        );
        lastSyncKeyRef.current = "";
      }
    },
    [dispatch]
  );

  return null;
};

WencoCustomersOnMap.propTypes = {
  shipments: PropTypes.shape({}).isRequired,
  drivers: PropTypes.shape({}),
  dispatch: PropTypes.func.isRequired,
  active: PropTypes.bool
};

WencoCustomersOnMap.defaultProps = {
  active: true,
  drivers: {}
};

const mapStateToProps = state => ({
  drivers: getDrivers(state)
});

export default connect(mapStateToProps)(WencoCustomersOnMap);
